import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { FormService } from 'src/app/shared/services/form.service';
import { WorksheetResponse } from 'src/app/features/worksheet/models/worksheet-response.modal';
import { MatDialog } from '@angular/material/dialog';
import { NotesComponent } from 'src/app/shared/components/notes/notes.component';
import { InstructionsComponent } from 'src/app/shared/components/instructions/instructions.component';
import { ProjectDetailsComponent } from 'src/app/shared/components/project-details/project-details.component';
import { MatTableDataSource } from '@angular/material/table';
import { AssignToLabtechComponent } from 'src/app/shared/components/assign-to-labtech/assign-to-labtech.component';
import { UserService } from 'src/app/shared/services/user.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { INotes } from 'src/app/features/models/inotes.modal';
import { SharedService } from 'src/app/features/service/shared.service';
import { NativeSoilService } from 'src/app/features/worksheet/services/native-soil/native-soil.service';
import { NativeSoilType } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';

@Component({
    selector: 'app-single-proctor-worksheet',
    templateUrl: './single-proctor-worksheet.component.html',
    styleUrls: ['./single-proctor-worksheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SingleProctorWorksheetComponent {
@Output() assigned = new EventEmitter<boolean>();
  @Output() rowDeletionSuccess = new EventEmitter<void>();
  selection = new SelectionModel<NativeSoilType>(true,[]);
  
  hasUnsavedChanges = false;
  projectId: string | null = null;
  private readonly destroyed$ = new Subject<boolean>();
  public dataSource = new MatTableDataSource<NativeSoilType>();
  public worksheets: NativeSoilType[];
  public worksheetRecords: WorksheetResponse | null = null;
  public note: INotes | undefined;
  userRoles: string[];
  daysLeft: number = 0;
  noteText: string | undefined = '';
  columns = [
    { key: 'select', displayName: '' },
    { key: 'sampleId', displayName: 'Sample Id' },
    { key: 'depth', displayName: 'Depth' },
    { key: 'bulkDensity', displayName: 'Bulk Density' },
    { key: 'dryDensity', displayName: 'Dry Density' },
    { key: 'proctorMouldWeight', displayName: 'Proctor Mould Weight' },
    { key: 'mouldAndSoilWeight', displayName: 'Mould And Soil Weight' },
    { key: 'soilNetWeight', displayName: 'Net Soil Weight (gm)' },
    { key: 'proctorMoldDiameter', displayName: 'Proctor Mold Diameter' },
    { key: 'gramPerCmCube', displayName: 'Grams per Cubic Centimeter' },
    { key: 'assignedTo', displayName: 'Assigned To' },
    { key: 'status', displayName: 'Status' },
    { key: 'action', displayName: '' }
  ];
  displayedColumns = this.columns.map(c => c.key);
  rowData: NativeSoilType[];
  isInstructionsConfirmed: boolean;

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private worksheetService: WorksheetService,
    private formService: FormService,
    private sharedService: SharedService,
    public dialog: MatDialog,
    public userService: UserService,
    private snackBar: MatSnackBar,
    private nativeSoilService: NativeSoilService,
  ) { }
  
  canDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedChanges) {
      return new Observable<boolean>(observer => {
        observer.next(true);
        observer.complete();
      });
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'Single-Proctor-Worksheet',
      data: {
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to leave without saving?',
        cancelText: 'Stay',
        confirmText: 'Leave'
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    return dialogRef.afterClosed().pipe(
      map(result => result === 'YES')
    );
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    this.fetchWorksheet(this.projectId ?? '');
    this.fetchWorksheetNotes(this.projectId ?? '');
    this.fetchWorksheetDetails(this.projectId ?? '');
    this.userAccountAndRole();
  }

  viewSubmissionForm(): void {
    const params = {
      f: 'w',
    };
    const navigationExtras: NavigationExtras = {
      queryParams: params,
      state: {
        isShipmentReceived: this.worksheetRecords?.project?.storageCompartment
      }
    };

    this.formService.updateData(null);
    this.router.navigate([`/review/form/nativesoil/${this.projectId}`], navigationExtras);
  }

  userAccountAndRole() {
    this.userService.userRole?.subscribe({
      next: (roles) => {
        this.userRoles = roles || [];
      }
    });
  }
  
  preventNumberInputChange(event: WheelEvent): void {
    (event.target as HTMLInputElement).blur();
  }

  toggleSelection(row: NativeSoilType) {
    this.selection.toggle(row);
  }

  isAllSelected() {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  isIndeterminate() {
    return this.selection.selected.length > 0 && !this.isAllSelected();
  }

  isMultipleSelected(row: any): boolean {
    return this.selection.selected.length >= 1 && this.selection.selected.includes(row);
  }

  formatCollectionDate(value: any): string {
    return DateTimeUtils.formatDateAndTime(value, 'MM/dd/yyyy');
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  printTargetWeights() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/native-soil-print'],
        { queryParams: { projectId: this.projectId } }
      )
    );

    window.open(url, '_blank');
  }

  isLastRowOfSample(row: NativeSoilType, index: number): boolean {
    const currentSampleId = row.sampleId;
    const nextRow = this.dataSource.data[index + 1];

    if (!nextRow) return false;
    return nextRow.sampleId !== currentSampleId;
  }

  onFocusClearZero(obj: any, field: string) {
    if (obj[field] === 0) {
      obj[field] = null;
    }
  }

  onBlurRestoreZero(obj: any, field: string) {
    if (obj[field] === null || obj[field] === '') {
      obj[field] = 0;
    }
  }

  calculateRowStatus(row: any): boolean {
    row.status = Number(row.proctorMouldWeight ?? 0) > 0 && Number(row.mouldAndSoilWeight ?? 0) > 0 && Number(row.proctorMoldDiameter ?? 0) > 0;
    return row.status;
  }

  focusNextRow(event: Event, currentRowId: string, fieldType: string): void {
    event.preventDefault();

    const currentIndex = this.dataSource.data.findIndex(
      x => x.id === currentRowId
    );

    if (currentIndex === -1) {
      return;
    }

    const nextRow = this.dataSource.data[currentIndex + 1];

    if (!nextRow) {
      return;
    }

    setTimeout(() => {
      const nextInput = document.querySelector(
        `[data-row-id="${nextRow.id}"][data-type="${fieldType}"]`
      ) as HTMLInputElement;

      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    });
  }

  saveTable(): void {
    const payload = this.preparePayload(this.dataSource.data);
    this.worksheetService.saveSingleProctorWorksheetTable(payload).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;
        this.snackBar.open('Saved successfully', 'DONE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
        });
        this.ngOnInit();
      },
      error: (err: any) => {
        this.snackBar.open('Save failed', 'DONE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
        });
        console.error('Save failed', err);
      }
    });
  }

  preparePayload(data: any[]): any[] {
    return data.map(row => ({
      projectId: this.projectId,
      id: row.id,
      proctorMouldWeight: Number(row.proctorMouldWeight ?? 0),
      mouldAndSoilWeight: Number(row.mouldAndSoilWeight ?? 0),
      proctorMoldDiameter: Number(row.proctorMoldDiameter ?? 0),
      status: this.calculateRowStatus(row),
    }));
  }

  fetchWorksheetNotes(projectId: string): void {
    this.sharedService.fetchWSNotes(projectId);
    this.sharedService.notes$.subscribe((data: any) => {
      this.note = data;
    });
    this.sharedService.notesText$.subscribe((data: string | undefined) => {
      this.noteText = data;
    });
  }

  fetchWorksheetDetails(projectId: string): void {
    this.formService.fetchFormData(projectId, 'nativesoil').subscribe({
      next: (result) => {
        const project = result?.project;
        const company = result?.company;
        const comments = result?.comments;
        this.worksheetRecords = {
          project,
          company,
          comments
        };
        if(this.worksheetRecords.project.dueDate) {
          const dueDate = new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(this.worksheetRecords.project.dueDate));
          this.daysLeft = this.findDateDifference(new Date(), dueDate);
        }
        this.isInstructionsConfirmed = this.worksheetRecords.project?.instructionsConfirmed ?? false;
      },
    });
  }

  calculateNetSoilWeight(element: any) {
    element.soilNetWeight = Number(element.mouldAndSoilWeight ?? 0) - Number(element.proctorMouldWeight ?? 0);
  }

  calculateGramPerCmCube(element: any): void {
    const inchToCm = 2.54;
    const proctorMoldHeightInches = 4.584;

    if (!element.proctorMoldDiameter) { 
      element.gramPerCmCube = 0; 
      return; 
    }

    const diameterCm = Number(element.proctorMoldDiameter) * inchToCm;
    const heightCm = proctorMoldHeightInches * inchToCm;

    const moldVolumeCm3 = Math.PI * (diameterCm * diameterCm) / 4 * heightCm;

    const soilNetWeight = Number(element.soilNetWeight ?? 0);
    element.gramPerCmCube = moldVolumeCm3 > 0 ? +(soilNetWeight / moldVolumeCm3) : 0;
  }

  calculateBulkDensity(element: any): void {
    const density = 62.4;
    element.bulkDensity = +(Number(element.gramPerCmCube ?? 0) * density);
  }

  updateCalculations(element: any, pageLoad: boolean = false) {
    if(!pageLoad){
      this.hasUnsavedChanges = true;
    }
    this.calculateNetSoilWeight(element);
    this.calculateGramPerCmCube(element);
    this.calculateBulkDensity(element);
    this.calculateRowStatus(element);
  }

  openViewNotesDialog(): void {
    const messageObject = {
      projectId: this.projectId,
      notes: this.note,
    };
    this.dialog
      .open(NotesComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetNotes(this.projectId ?? '');
      });
  }

  openViewInstructionsDialog(): void {
    if (!this.worksheetRecords?.comments) return;

    this.dialog.open(InstructionsComponent, {
      panelClass: 'app-dialog',
      data: this.worksheetRecords.comments,
      disableClose: true,
      width: '50%',
    });
  }

  openProjectDetailsComponent(title: string, fieldName: string): void {
    if (!this.worksheetRecords?.project) return;
    const project = this.worksheetRecords.project;
    const messageObject = {
      projectId: this.projectId,
      title,
      fieldName,
      ovenDate: project.ovenDate,
      wetTestData: project.wetTestData,
      dryTestData: project.dryTestData,
    };
    this.dialog
      .open(ProjectDetailsComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetDetails(this.projectId ?? '');
      });
  }

  openAssignToDialog(worksheetId: string): void {
    if (this.hasUnsavedChanges) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'as-received-worksheet',
        data: {
          title: 'Unsaved Changes',
          content: 'You have unsaved changes. Do you want to continue assigning without saving your changes?',
          cancelText: 'Cancel',
          confirmText: 'Continue'
        },
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'YES') {
          this.openAssignDialogInternal(worksheetId);
        }
      });
      return;
    }
    
    this.openAssignDialogInternal(worksheetId);
  }

  asReceivedWorksheet(): void {
    this.navigateToWorksheet('as-received-worksheet');
  }

  moistureConditioningWorksheet(): void {
    this.navigateToWorksheet('moisture-conditioning-worksheet');
  }

  singleProctorWorksheet(): void {
    this.navigateToWorksheet('single-proctor-worksheet');
  }

  compactionWorksheet(): void {
    this.navigateToWorksheet('compaction-worksheet');
  }

  thermalResistivityWorksheet(): void {
    this.navigateToWorksheet('native-soil-worksheet');
  }

  private navigateToWorksheet(route: string): void {
    this.canDeactivate().subscribe((canLeave: boolean) => {
      if (canLeave) {
        this.openWorksheet(route);
      }
    });
  }

  openWorksheet(route: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [route],
        {
          queryParams: {
            projectId: this.projectId
          }
        }
      )
    );

    window.location.href = url;
  }

  openAssignDialogInternal(worksheetId: string): void {
    this.dialog
      .open(AssignToLabtechComponent, {
        data: {
          worksheetId,
          type: 'proctorWorksheets',
          multipleSelected : this.isMultipleSelected(worksheetId),
          worksheetRows : this.selection.selected,
          totalNumOfWorksheets: this.dataSource.data.length,
          role: this.userRoles
        },
        disableClose: true,
        width: '40%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((assigned) => {
        if (assigned) {
          this.fetchWorksheet(this.projectId ?? '');
          this.rowDeletionSuccess.emit();
          this.assigned.emit(true);
          this.selection.clear();
          this.hasUnsavedChanges = false;
        }
      });
  }

  findDateDifference(startDate: Date, endDate: Date) {
    const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return Math.trunc(dayDifference);
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  setDepth(elem: any): string {
    let depthRange = '';
    if (elem.depth !== null) {
      depthRange = `${elem.depth}`;
    }
    if (elem.endDepth) {
      if (elem.depth !== null && elem.endDepth === null) {
        depthRange = `${elem.depth}`;
      }
      if (elem.endDepth !== null && elem.depth !== null) {
        depthRange = `${elem.depth}-${elem.endDepth}`;
      }
    }
    return depthRange;
  }

  private mapUser(worksheet: any): string | null {
    if (worksheet.assignedToId) {
      return `${worksheet.assignedTo.firstName} ${worksheet.assignedTo.lastName}`;
    }
    return null;
  }

  fetchWorksheet(projectId: string): void {
    this.nativeSoilService.getNativeSoilWorkSheet(projectId).subscribe({
      next: (response: any) => {
        let initial = 1;
        this.worksheets = response?.proctorWorksheets;
        this.worksheets.forEach((worksheet: any) => {
          if (!worksheet.groupOrder) {
            initial += 1;
          }
          worksheet.sequence = initial;
          if (worksheet.collectionDate) {
            worksheet.collectionDate = DateTimeUtils.formatDateAndTime(worksheet.collectionDate, 'MM/dd/yyyy');
          }
          worksheet.displayDepth = this.setDepth(worksheet);
          worksheet.assignedTo = this.mapUser(worksheet);

          // Default Proctor Mold Diameter
          if (worksheet.proctorMoldDiameter == null) {
            worksheet.proctorMoldDiameter = 4;
          }
        });
        this.dataSource.data = this.worksheets;
        this.dataSource.data.forEach(e => this.updateCalculations(e, true));
        this.rowData = this.dataSource.data;
      },
      error: (err) => {
        console.error('Error fetching worksheets:', err);
      },
    });
  }
}
